import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowDownLeft,
  ArrowUpRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Eye,
  Pencil,
  Plus,
  Scale,
  Trash2,
  UserRound,
  UsersRound,
} from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api, { apiErrorText } from "../lib/api";
import { currentNepalMonth, monthAdd, monthLabel } from "../lib/partyBook";
import { fmtTeamCredit, TEAM_CREDIT_CURRENCIES } from "../lib/teamCredit";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

const EMPTY_TEAM = { name: "", notes: "" };

export default function TeamCredit() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState("all");
  const [month, setMonth] = useState(currentNepalMonth());
  const requestedCurrency = searchParams.get("currency");
  const [currency, setCurrency] = useState(TEAM_CREDIT_CURRENCIES.includes(requestedCurrency) ? requestedCurrency : "NPR");
  const [dashboard, setDashboard] = useState({ summary: {}, teams: [] });
  const [loading, setLoading] = useState(true);
  const [teamOpen, setTeamOpen] = useState(false);
  const [teamForm, setTeamForm] = useState(EMPTY_TEAM);
  const [editingTeam, setEditingTeam] = useState(null);
  const [deletingTeam, setDeletingTeam] = useState(null);
  const [savingTeam, setSavingTeam] = useState(false);
  const [deletingTeamBusy, setDeletingTeamBusy] = useState(false);

  const filterParams = useMemo(() => {
    const params = { period, currency };
    if (period === "monthly") params.month = month;
    return params;
  }, [period, month, currency]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const response = await api.get("/team-credit/dashboard", { params: filterParams });
      setDashboard(response.data);
    } catch (error) {
      toast.error(apiErrorText(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams]);

  const saveTeam = async () => {
    if (!teamForm.name.trim()) return toast.error("Team name is required");
    setSavingTeam(true);
    try {
      const payload = {
        name: teamForm.name.trim(),
        notes: teamForm.notes.trim() || null,
      };
      const response = editingTeam?.id
        ? await api.put(`/team-credit/teams/${editingTeam.id}`, payload)
        : await api.post("/team-credit/teams", payload);
      toast.success(editingTeam?.id ? "Team updated" : "Team created");
      setTeamOpen(false);
      setTeamForm(EMPTY_TEAM);
      setEditingTeam(null);
      await loadDashboard();
      if (!editingTeam?.id) navigate(`/team-credit/${response.data.id}?currency=${currency}`);
    } catch (error) {
      toast.error(apiErrorText(error));
    } finally {
      setSavingTeam(false);
    }
  };

  const openCreateTeam = () => {
    setEditingTeam(null);
    setTeamForm(EMPTY_TEAM);
    setTeamOpen(true);
  };

  const openEditTeam = (team) => {
    setEditingTeam(team);
    setTeamForm({ name: team.name || "", notes: team.notes || "" });
    setTeamOpen(true);
  };

  const deleteTeam = async () => {
    if (!deletingTeam?.id) return;
    setDeletingTeamBusy(true);
    try {
      await api.delete(`/team-credit/teams/${deletingTeam.id}`, { params: { cascade: true } });
      toast.success("Team and linked records deleted");
      setDeletingTeam(null);
      await loadDashboard();
    } catch (error) {
      toast.error(apiErrorText(error));
    } finally {
      setDeletingTeamBusy(false);
    }
  };

  const summary = dashboard.summary || {};
  const teams = dashboard.teams || [];
  const filterLabel = period === "all" ? "All Time" : monthLabel(month);
  const selectCurrency = (value) => {
    setCurrency(value);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("currency", value);
    setSearchParams(nextParams, { replace: true });
  };
  const openTeam = (teamId) => navigate(`/team-credit/${teamId}?currency=${currency}`);

  return (
    <Layout
      title="Teams Credit"
      subtitle="Team and member-wise NPR, USD, and USDT credit ledger · Nepal time"
      testId="team-credit-page"
      actions={
        <Button onClick={openCreateTeam} className="h-9 bg-zinc-900 text-white hover:bg-zinc-800">
          <Plus size={14} className="mr-1.5" /> Create team
        </Button>
      }
    >
      <PeriodFilter period={period} setPeriod={setPeriod} month={month} setMonth={setMonth} currency={currency} setCurrency={selectCurrency} />

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SummaryCard label="Total Credit" value={summary.total_credit} currency={currency} icon={ArrowDownLeft} tone="emerald" footer={`${summary.entry_count || 0} entries · ${filterLabel}`} />
        <SummaryCard label="Total Debit" value={summary.total_debit} currency={currency} icon={ArrowUpRight} tone="rose" footer={`${summary.member_count || 0} members · ${filterLabel}`} />
        <SummaryCard label="Net Credit" value={summary.net_credit} currency={currency} icon={Scale} tone="sky" footer={`Credit − Debit · ${filterLabel}`} />
      </div>

      <section className="stat-card overflow-hidden" data-testid="team-credit-list">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b hairline px-5 py-4">
          <div>
            <h2 className="font-display text-[16px] font-semibold text-zinc-900">Teams</h2>
            <p className="mt-0.5 text-[12px] text-zinc-500">{currency} totals for {filterLabel.toLowerCase()}</p>
          </div>
          <div className="flex items-center gap-2 text-[12px] text-zinc-500">
            <UsersRound size={14} /> {summary.team_count || 0} teams
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="border-b hairline bg-zinc-50/50 text-left text-[10.5px] uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3 font-medium">Team</th>
                <th className="px-4 py-3 text-right font-medium">Members</th>
                <th className="px-4 py-3 text-right font-medium">Credit</th>
                <th className="px-4 py-3 text-right font-medium">Debit</th>
                <th className="px-4 py-3 text-right font-medium">Net</th>
                <th className="px-4 py-3 text-right font-medium">Entries</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">Loading teams...</td></tr>
              ) : teams.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-14 text-center text-zinc-500">
                    <UserRound size={22} strokeWidth={1.5} className="mx-auto mb-2 text-zinc-400" />
                    No credit teams yet.
                    <button onClick={openCreateTeam} className="ml-1 font-medium text-zinc-900 underline underline-offset-2">Create the first team</button>
                  </td>
                </tr>
              ) : teams.map((team) => (
                <tr key={team.id} className="row-hover">
                  <td className="px-4 py-3">
                    <button onClick={() => openTeam(team.id)} className="text-left">
                      <div className="font-medium text-zinc-900 hover:underline">{team.name}</div>
                      <div className="max-w-[300px] truncate text-[11px] text-zinc-500">{team.notes || "No team note"}</div>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{team.member_count || 0}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-700">{fmtTeamCredit(team.total_credit, currency)}</td>
                  <td className="px-4 py-3 text-right font-mono text-rose-700">{fmtTeamCredit(team.total_debit, currency)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${Number(team.net_credit) < 0 ? "text-rose-700" : "text-zinc-900"}`}>{fmtTeamCredit(team.net_credit, currency)}</td>
                  <td className="px-4 py-3 text-right font-mono">{team.entry_count || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openTeam(team.id)} className="rounded p-1.5 text-zinc-600 transition-colors hover:bg-zinc-100" title="Open team ledger" aria-label={`Open ${team.name} ledger`}><Eye size={14} strokeWidth={1.6} /></button>
                      <button onClick={() => openEditTeam(team)} className="rounded p-1.5 text-zinc-600 transition-colors hover:bg-zinc-100" title="Edit team" aria-label={`Edit ${team.name}`}><Pencil size={14} strokeWidth={1.6} /></button>
                      <button onClick={() => setDeletingTeam(team)} className="rounded p-1.5 text-rose-600 transition-colors hover:bg-rose-50" title="Delete team" aria-label={`Delete ${team.name}`}><Trash2 size={14} strokeWidth={1.6} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Dialog open={teamOpen} onOpenChange={(open) => { setTeamOpen(open); if (!open) setEditingTeam(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{editingTeam ? "Edit credit team" : "Create credit team"}</DialogTitle>
            <DialogDescription>{editingTeam ? "Update the team name or internal note without changing its ledger history." : "Create the team first, then add its members and ledger entries."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Team name *</Label>
              <Input value={teamForm.name} onChange={(event) => setTeamForm({ ...teamForm, name: event.target.value })} placeholder="Team name" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={teamForm.notes} onChange={(event) => setTeamForm({ ...teamForm, notes: event.target.value })} placeholder="Optional team note" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTeamOpen(false)}>Cancel</Button>
            <Button onClick={saveTeam} disabled={savingTeam} className="bg-zinc-900 text-white hover:bg-zinc-800">{savingTeam ? "Saving..." : editingTeam ? "Update team" : "Create team"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deletingTeam)} onOpenChange={(open) => !open && !deletingTeamBusy && setDeletingTeam(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deletingTeam?.name || "this team"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the team, {deletingTeam?.member_count || 0} linked members, and {deletingTeam?.all_entry_count || 0} ledger entries across all currencies and dates. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingTeamBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteTeam} disabled={deletingTeamBusy} className="bg-rose-600 text-white hover:bg-rose-700">{deletingTeamBusy ? "Deleting..." : "Delete team and records"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function PeriodFilter({ period, setPeriod, month, setMonth, currency, setCurrency }) {
  return (
    <div className="stat-card mb-5 flex flex-wrap items-center gap-2 p-3">
      <Select value={period} onValueChange={setPeriod}>
        <SelectTrigger className="h-8 w-[150px]" data-testid="team-credit-period-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Time</SelectItem>
          <SelectItem value="monthly">Monthly</SelectItem>
        </SelectContent>
      </Select>
      <Select value={currency} onValueChange={setCurrency}>
        <SelectTrigger className="h-8 w-[120px]" data-testid="team-credit-currency-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          {TEAM_CREDIT_CURRENCIES.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
        </SelectContent>
      </Select>
      {period === "monthly" && (
        <>
          <button onClick={() => setMonth(monthAdd(month, -1))} className="flex h-8 w-8 items-center justify-center rounded-md border hairline bg-white transition-colors hover:bg-zinc-50" aria-label="Previous month"><ChevronLeft size={14} /></button>
          <div className="min-w-[130px] px-2 text-center font-display text-[14px] font-semibold">{monthLabel(month)}</div>
          <button onClick={() => setMonth(monthAdd(month, 1))} className="flex h-8 w-8 items-center justify-center rounded-md border hairline bg-white transition-colors hover:bg-zinc-50" aria-label="Next month"><ChevronRight size={14} /></button>
          {month !== currentNepalMonth() && <button onClick={() => setMonth(currentNepalMonth())} className="h-8 rounded-md border hairline bg-white px-3 text-[12px] hover:bg-zinc-50">This month</button>}
        </>
      )}
      <div className="ml-auto flex items-center gap-1.5 text-[11.5px] text-zinc-500"><CalendarDays size={13} /> Asia/Kathmandu</div>
    </div>
  );
}

function SummaryCard({ label, value, currency, icon: Icon, tone, footer }) {
  const tones = {
    emerald: { icon: "bg-emerald-50 text-emerald-600", value: "text-emerald-700" },
    rose: { icon: "bg-rose-50 text-rose-600", value: "text-rose-700" },
    sky: { icon: "bg-sky-50 text-sky-600", value: Number(value) < 0 ? "text-rose-700" : "text-sky-950" },
  };
  const selected = tones[tone] || tones.sky;
  return (
    <div className="stat-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] uppercase tracking-widest text-zinc-500">{label}</span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-md ${selected.icon}`}><Icon size={16} strokeWidth={1.7} /></span>
      </div>
      <div className={`mt-3 font-mono text-[23px] font-medium ${selected.value}`}>{fmtTeamCredit(value, currency)}</div>
      <div className="mt-1.5 text-[10.5px] text-zinc-400">{footer}</div>
    </div>
  );
}
